import { WS_PROTOCOL_VERSION } from '@badger-sentinel/protocol';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { ApiConfigError, readApiConfig } from './api/config.js';
import { DEMO_BASE_URL, DEMO_WS_URL, isDemoRequested } from './demo/config.js';
import { useDemoBridge } from './demo/useDemoBridge.js';
import { fetchHealth, fetchVersion } from './api/health.js';
import { ConfigErrorScreen, ProtocolMismatchScreen } from './game/chrome.js';
import { SeatConsole } from './game/SeatConsole.js';
import { LobbyScreen } from './game/LobbyScreen.js';
import {
  readStoredSeat,
  readStoredSession,
  storeSeat,
  storeSession,
  type ClaimedSeat,
  type JoinedSession,
} from './game/session.js';
import { useGameSocket } from './ws/useGameSocket.js';

/**
 * Milestone 0 exit criterion, made visible (§18 M0):
 *
 *   "A browser on Pages talks to the API over WSS with no CORS or mixed-content
 *    errors."
 *
 * Since M5 the entry point is the console shell (entry → seat select → seat
 * view); the M0 connectivity panel moves behind `?diagnostics=1` (M2-D11 —
 * "verifying a deploy from a phone in the room, without devtools" is still
 * worth keeping, it just isn't what a player sees first anymore).
 *
 * `?demo` is the third door (`demo/config.ts`): the whole server runs in a worker
 * in this tab, so there is no API to configure, no version to negotiate, and
 * nothing that can be down. Everything below it — lobby, seat select, console —
 * is the same code on the same schemas, pointed at a different base URL.
 */
export function App(): JSX.Element {
  /**
   * Two ways in, and they are not the same thing.
   *
   * `?demo` is known before the first render, so the app waits for the worker
   * before showing anything. Typing `BSNT-DEMO` at entry activates it mid-flow —
   * `demo/activate.ts` has already started and awaited the worker by the time
   * this flips, so there is nothing to wait for and nothing to unmount.
   */
  const demoFromUrl = useMemo(() => isDemoRequested(), []);
  const [demoActivated, setDemoActivated] = useState(false);
  const demo = demoFromUrl || demoActivated;
  const demoBridge = useDemoBridge(demoFromUrl);

  const [claimed, setClaimed] = useState<ClaimedSeat | null>(() => readStoredSeat());
  const [session, setSession] = useState<JoinedSession | null>(() =>
    readStoredSeat() ? null : readStoredSession(),
  );
  const diagnostics = useMemo(() => {
    try {
      return new URLSearchParams(window.location.search).has('diagnostics');
    } catch {
      return false;
    }
  }, []);

  const config = useMemo(() => {
    // The demo needs no build-time API URL, and must not fail for want of one:
    // its "server" is the worker this page just started.
    if (demo) {
      return { ok: true as const, value: { baseUrl: DEMO_BASE_URL, wsUrl: DEMO_WS_URL } };
    }
    try {
      return { ok: true as const, value: readApiConfig(import.meta.env) };
    } catch (error) {
      return {
        ok: false as const,
        message: error instanceof ApiConfigError ? error.message : String(error),
      };
    }
  }, [demo]);

  // Hooks must run unconditionally, so the queries are declared before the early
  // return below and simply disabled when there is no usable base URL.
  const baseUrl = config.ok ? config.value.baseUrl : null;
  const socket = useGameSocket(diagnostics && config.ok ? config.value.wsUrl : null);

  const health = useQuery({
    queryKey: ['health', baseUrl],
    enabled: baseUrl !== null && diagnostics,
    refetchInterval: 10_000,
    queryFn: ({ signal }) => fetchHealth(baseUrl ?? '', signal),
  });

  // No abort signal, deliberately. React's StrictMode double-mount unsubscribes
  // the observer between the two mounts, and TanStack Query aborts an in-flight
  // fetch whose signal the query function consumed — which is the whole of the
  // `/api/version` `net::ERR_ABORTED` seen on every dev page load. The guard was
  // never dead (the second mount's request answers, and production does not
  // double-mount at all), but a version probe is two hundred bytes and there is
  // nothing worth cancelling, so the console is now quiet as well as correct.
  const version = useQuery({
    queryKey: ['version', baseUrl],
    enabled: baseUrl !== null,
    queryFn: () => fetchVersion(baseUrl ?? ''),
  });

  // Nothing can be fetched before the worker answers, so the app waits rather
  // than rendering an entry form whose first request would fail.
  if (demoFromUrl && demoBridge.status !== 'ready') {
    if (demoBridge.status === 'failed') {
      return <ConfigErrorScreen message={demoBridge.error ?? 'The demo could not start.'} />;
    }
    return <></>;
  }

  if (config.ok && claimed) {
    return (
      <SeatConsole
        baseUrl={config.value.baseUrl}
        wsUrl={config.value.wsUrl}
        gameId={claimed.gameId}
        seatToken={claimed.seatToken}
        scenario={claimed.scenario}
        sides={claimed.sides}
        onLeave={() => {
          storeSeat(null);
          setClaimed(null);
        }}
      />
    );
  }

  if (!config.ok) {
    return <ConfigErrorScreen message={config.message} />;
  }

  // 426 protocol mismatch (entry.md): a proactive guard rather than waiting for a
  // REST call to fail with it — "needs a hard reload, so give it a screen not a
  // toast", and there's no point letting someone fill in a passphrase first.
  if (!diagnostics && version.data && version.data.protocolVersion !== WS_PROTOCOL_VERSION) {
    return (
      <ProtocolMismatchScreen
        serverVersion={version.data.protocolVersion}
        clientVersion={WS_PROTOCOL_VERSION}
      />
    );
  }

  // Entry and seat select are one screen (game/LobbyScreen.tsx) — the console
  // shell keeps its chrome mounted across the join so the handoff can animate
  // rather than cut. It mounts straight into the roster when a session is
  // already stored, which is the reload-mid-lobby path.
  if (!diagnostics) {
    return (
      <LobbyScreen
        baseUrl={config.value.baseUrl}
        session={session}
        onJoined={(joined) => {
          storeSession(joined);
          setSession(joined);
        }}
        onClaimed={(next) => {
          storeSession(null);
          setSession(null);
          setClaimed(next);
        }}
        onExit={() => {
          storeSession(null);
          setSession(null);
        }}
        onSessionExpired={() => {
          storeSession(null);
          setSession(null);
        }}
        onDemoActivated={() => setDemoActivated(true)}
      />
    );
  }

  const httpOk = health.isSuccess;
  const wsOk = socket.status === 'open' && socket.hello !== null;
  const clockOk = socket.clockSamples > 0;
  const secure = config.value.baseUrl.startsWith('https://') || isLocalDev(config.value.baseUrl);

  return (
    <Shell>
      <Panel title="Milestone 0 — connectivity" tone={httpOk && wsOk && clockOk ? 'good' : 'warn'}>
        <ul className="space-y-2 text-sm">
          <Check ok={secure} label="API origin is HTTPS (no mixed content from a Pages page)" />
          <Check ok={httpOk} label="REST reachable cross-origin (CORS allows this Pages origin)" />
          <Check ok={wsOk} label="WebSocket upgrade accepted over WSS" />
          <Check ok={clockOk} label="Server clock offset established" />
        </ul>
      </Panel>

      <div className="grid gap-4 md:grid-cols-2">
        <Panel title="Endpoints">
          <Row label="API base" value={config.value.baseUrl} />
          <Row label="Socket" value={config.value.wsUrl} />
          <Row label="Page origin" value={window.location.origin} />
        </Panel>

        <Panel
          title="Health"
          tone={health.data?.status === 'ok' ? 'good' : health.isError ? 'bad' : 'warn'}
        >
          {health.isPending && <Muted>checking…</Muted>}
          {health.isError && <Muted>{describeError(health.error)}</Muted>}
          {health.data && (
            <>
              <Row label="Status" value={health.data.status} />
              <Row
                label="Database"
                value={`${health.data.db.status}${
                  health.data.db.latencyMs === undefined ? '' : ` · ${health.data.db.latencyMs} ms`
                }`}
              />
              <Row label="Uptime" value={formatDuration(health.data.uptimeMs)} />
            </>
          )}
        </Panel>

        <Panel title="Build & rule content">
          {version.data ? (
            <>
              <Row label="Server" value={version.data.version} />
              <Row label="Commit" value={version.data.gitSha ?? '—'} />
              <Row label="Rules content" value={version.data.contentVersion} />
              <Row label="Content hash" value={`${version.data.contentHash.slice(0, 16)}…`} />
              <Row label="WS protocol" value={`v${version.data.protocolVersion}`} />
            </>
          ) : (
            <Muted>{version.isError ? describeError(version.error) : 'checking…'}</Muted>
          )}
        </Panel>

        <Panel title="Socket" tone={wsOk ? 'good' : socket.status === 'closed' ? 'bad' : 'warn'}>
          <Row label="State" value={socket.status} />
          <Row label="Connection" value={socket.hello?.connectionId ?? '—'} />
          <Row
            label="Clock offset"
            value={
              clockOk ? `${socket.clockOffsetMs >= 0 ? '+' : ''}${socket.clockOffsetMs} ms` : '—'
            }
          />
          <Row
            label="Round trip"
            value={socket.roundTripMs === null ? '—' : `${socket.roundTripMs} ms`}
          />
          {socket.reconnectAttempts > 0 && (
            <Row label="Reconnects" value={String(socket.reconnectAttempts)} />
          )}
          {socket.lastError !== null && <Muted>{socket.lastError}</Muted>}
        </Panel>
      </div>

      <ServerClock offsetMs={socket.clockOffsetMs} synced={clockOk} />

      {socket.serverErrors.length > 0 && (
        <Panel title="Server errors" tone="bad">
          <ul className="space-y-1 text-sm">
            {socket.serverErrors.map((error, index) => (
              <li key={`${error.code}-${index}`}>
                <Code>{error.code}</Code> {error.message}
              </li>
            ))}
          </ul>
        </Panel>
      )}
    </Shell>
  );
}

/**
 * §8.2: every deadline in this game is an absolute epoch millisecond on the server
 * clock. This is that clock, corrected — the same number every seat will see.
 */
function ServerClock({ offsetMs, synced }: { offsetMs: number; synced: boolean }): JSX.Element {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(timer);
  }, []);

  return (
    <Panel title="Server clock (§8.2)">
      <div className="font-mono text-3xl tabular-nums text-neutral-100">
        {new Date(now + offsetMs).toISOString().slice(11, 23)}
      </div>
      <p className="mt-2 text-xs text-neutral-500">
        {synced
          ? 'Corrected against the server via WebSocket ping/pong. Countdowns render against this, never against a local duration.'
          : 'Uncorrected — showing this browser’s clock until the first pong arrives.'}
      </p>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Presentation primitives. Deliberately minimal — §13 is explicit that visual
// design is not settled, and M5 replaces this wholesale.
// ---------------------------------------------------------------------------

function Shell({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <main className="mx-auto flex min-h-screen max-w-4xl flex-col gap-4 p-6">
      <header className="mb-2">
        <h1 className="text-2xl font-semibold tracking-tight text-neutral-100">Badger Sentinel</h1>
        <p className="text-sm text-neutral-500">Foundations check · Milestone 0</p>
      </header>
      {children}
      <footer className="mt-auto pt-6 text-xs text-neutral-600">
        Static SPA on GitHub Pages · authoritative game server on Oracle Cloud
      </footer>
    </main>
  );
}

type Tone = 'good' | 'warn' | 'bad' | 'neutral';

const TONE_RING: Record<Tone, string> = {
  good: 'ring-emerald-800/60',
  warn: 'ring-amber-800/60',
  bad: 'ring-red-800/60',
  neutral: 'ring-neutral-800',
};

function Panel({
  title,
  tone = 'neutral',
  children,
}: {
  title: string;
  tone?: Tone;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section className={`rounded-lg bg-neutral-900/60 p-4 ring-1 ${TONE_RING[tone]}`}>
      <h2 className="mb-3 text-xs font-semibold uppercase tracking-widest text-neutral-500">
        {title}
      </h2>
      {children}
    </section>
  );
}

function Row({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1 text-sm">
      <span className="shrink-0 text-neutral-500">{label}</span>
      <span className="truncate font-mono text-neutral-200" title={value}>
        {value}
      </span>
    </div>
  );
}

function Check({ ok, label }: { ok: boolean; label: string }): JSX.Element {
  return (
    <li className="flex items-start gap-2">
      <span className={ok ? 'text-emerald-400' : 'text-amber-400'} aria-hidden="true">
        {ok ? '✓' : '•'}
      </span>
      <span className={ok ? 'text-neutral-300' : 'text-neutral-400'}>{label}</span>
      <span className="sr-only">{ok ? '(passing)' : '(not yet passing)'}</span>
    </li>
  );
}

function Muted({ children }: { children: React.ReactNode }): JSX.Element {
  return <p className="text-sm text-neutral-500">{children}</p>;
}

function Code({ children }: { children: React.ReactNode }): JSX.Element {
  return <code className="rounded bg-neutral-800 px-1 py-0.5 font-mono text-xs">{children}</code>;
}

function isLocalDev(baseUrl: string): boolean {
  return /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/.test(baseUrl);
}

function describeError(error: unknown): string {
  if (error instanceof TypeError) {
    // fetch() rejects with an opaque TypeError and the browser deliberately
    // withholds the reason. Do not name one — CORS, mixed content, DNS, TLS, a
    // network block, and an API that is simply down are indistinguishable from
    // here, and asserting the wrong one sends the reader to the wrong system.
    // Note the CORS-header subtlety: a proxy that answers 502/503 because the
    // app behind it is down emits no Access-Control-Allow-Origin either, so a
    // dead server presents here exactly as a CORS misconfiguration does.
    return 'Request failed before a response could be read. Check the API host directly (curl its /healthz) to tell an unreachable or down API apart from a CORS or mixed-content rejection.';
  }
  return error instanceof Error ? error.message : String(error);
}

function formatDuration(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
  return `${seconds}s`;
}
